import { ReactElement, useState } from 'react';

import { BaseButtonType, Button } from '@displate/styleguide';

import { trackMorePostersBtn } from '@/common/analytics/MasonryFeeds/MasonryFeeds.analytics';
import { MasonryGridItem } from '@/common/components/MasonryFeed/MasonryGridItem';
import { ComponentMetadata } from '@/common/components/types';
import { GalleryItem } from 'generatedBfw/static';

import styles from './masonryFeed.module.scss';

interface MasonryFeedGridProps {
  items: Array<GalleryItem>;
  feedName: string;
  metadata: ComponentMetadata;
  infiniteScroll?: boolean;
  redirectButton: () => ReactElement;
}

export const DEFAULT_NUM_OF_ARTWORKS_TO_DISPLAY = 18;
const INFINITE_SCROLL_TOTAL_LIMIT = 5000;

export const MasonryFeedGrid = ({
  items,
  feedName,
  metadata,
  infiniteScroll,
  redirectButton,
}: MasonryFeedGridProps) => {
  const [pagination, setPagination] = useState<{ page: number; limit: number }>({
    page: 1,
    limit: infiniteScroll ? INFINITE_SCROLL_TOTAL_LIMIT : DEFAULT_NUM_OF_ARTWORKS_TO_DISPLAY,
  });

  const handleSeeMorePosters = () => {
    setPagination((prevState) => {
      const nextPage = prevState.page + 1;
      return { page: nextPage, limit: DEFAULT_NUM_OF_ARTWORKS_TO_DISPLAY * nextPage };
    });
    trackMorePostersBtn('see_more_posters_button');
  };

  return (
    <>
      <div className={styles.masonryFeedGridContainer}>
        {items.map((item, index) => {
          if (index <= pagination.limit - 1)
            return (
              <MasonryGridItem
                index={index}
                item={item}
                key={`${feedName}-${item.externalId}-${index}`}
                metadata={metadata}
              />
            );
        })}
      </div>

      {!infiniteScroll && (
        <>
          {pagination.limit >= (items?.length || 0) ? (
            <Button>{redirectButton()}</Button>
          ) : (
            <Button
              data-testid="masonry-feed-grid-see-more-btn"
              onClick={handleSeeMorePosters}
              variant={BaseButtonType.INVERTED}
            >
              See more posters
            </Button>
          )}
        </>
      )}
    </>
  );
};
